import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, FormControlLabel, Stack } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import CheckboxTree from '../_parts/CheckboxTree';
import { labelFontSemiBold } from '../../../../Theme';
import { IAdditionalInputOptionChoice } from '../../../../redux/project/sliceProjectWorkflow';

interface IContentProps {
    itemAliasValues: IAdditionalInputOptionChoice[];
    additionalInputState: string[];
    hasAdditionalInputError?: boolean;
    onAdditionalInputStateChanged: (newState: string[]) => void;
}

const createNewItemsList = (items: IAdditionalInputOptionChoice[]) => {
    const flatValueAliasTree = [];

    items.forEach(item => {
        const itemPathArray = item.value.split('\\');

        itemPathArray.reduceRight((acc, pathItem, index) => {
            const duplicateValue = flatValueAliasTree.find(
                treeItem => treeItem.path === itemPathArray.slice(0, index + 1).join('\\'),
            );

            if (!duplicateValue) {
                const treeitem = {
                    id: !flatValueAliasTree.find(valueAliasItem => valueAliasItem.id === item.alias)
                        ? item.alias
                        : uuidv4(),
                    label: pathItem,
                    path:
                        itemPathArray.length === index + 1
                            ? item.value
                            : itemPathArray.slice(0, index + 1).join('\\'),
                    parent: index - 1 >= 0 ? itemPathArray.slice(0, index).join('\\') : '',
                    hasCheckbox: itemPathArray.length === index + 1,
                };

                flatValueAliasTree.push(treeitem);

                acc.push(treeitem);
            }

            return acc;
        }, []);
    });

    return flatValueAliasTree;
};

export default ({
    itemAliasValues,
    additionalInputState,
    hasAdditionalInputError,
    onAdditionalInputStateChanged,
}: IContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'dialogParts' });

    const [fullCheckedTreeItemIds, setFullCheckedTreeItemIds] = useState<string[]>([]);

    useEffect(() => {
        const filteredItemValues = itemAliasValues
            .filter(itemAliasValue => additionalInputState.includes(itemAliasValue.alias))
            .map(item => item.value);

        setFullCheckedTreeItemIds(
            items.filter(item => filteredItemValues.includes(item.path)).map(item => item.id),
        );
    }, [additionalInputState]);

    const items = useMemo(() => createNewItemsList(itemAliasValues), []);

    const handleCheckNode = (id: string, isChecked: boolean) => {
        const valueAliasItem = items.find(item => item.id === id);

        const aliasValue = itemAliasValues.find(item => item.value === valueAliasItem.path).alias;

        const additionalInputValue = isChecked
            ? [...additionalInputState, aliasValue]
            : additionalInputState.filter(x => x !== aliasValue);

        onAdditionalInputStateChanged(additionalInputValue);
    };

    const checkableItems = items.filter(item => item.hasCheckbox);

    const handleSelectDeselectAll = (_, checked: boolean) => {
        onAdditionalInputStateChanged(
            checked ? [...new Set(itemAliasValues.map(item => item.alias))] : [],
        );
    };

    return (
        <Stack direction='column' spacing={'16px'} sx={{ width: '512px' }}>
            <FormControl>
                <Stack
                    direction='row'
                    sx={{
                        justifyContent: 'right',
                        alignItems: 'center',
                    }}
                >
                    <FormControlLabel
                        label={
                            fullCheckedTreeItemIds.length !== checkableItems.length
                                ? t('selectAll')
                                : t('clear')
                        }
                        labelPlacement='start'
                        sx={{
                            marginBottom: '8px',
                            '& .MuiFormControlLabel-label': {
                                fontFamily: labelFontSemiBold,
                                fontSize: '14px',
                            },
                        }}
                        control={
                            <Checkbox
                                sx={{ padding: 0, paddingX: '9px' }}
                                onChange={handleSelectDeselectAll}
                                checked={fullCheckedTreeItemIds.length === checkableItems.length}
                            />
                        }
                    />
                </Stack>

                <CheckboxTree
                    items={items}
                    checkedNodes={fullCheckedTreeItemIds}
                    onCheckNode={handleCheckNode}
                    hasError={hasAdditionalInputError}
                    withTreeExpansion
                />
            </FormControl>
        </Stack>
    );
};
